import React from "react";
import styled from "styled-components";
import { Link, NavLink } from "react-router-dom";
import { LuArrowLeft } from "react-icons/lu";
import usePageMeta from "../../hooks/usePageMeta";
import { LAST_UPDATED } from "../../legal/company";
import { legalPages } from "../../routes";
import { Container, Eyebrow } from "../ui";

const Header = styled.header`
  padding: calc(var(--nav-height) + 64px) 0 48px;
  background: linear-gradient(180deg, var(--color-bg-tint) 0%, var(--color-bg) 100%);
  border-bottom: 1px solid var(--color-border);

  @media (max-width: 768px) {
    padding: calc(var(--nav-height) + 40px) 0 36px;
  }
`;

const BackLink = styled(Link)`
  display: flex;
  width: fit-content;
  align-items: center;
  gap: 6px;
  margin-bottom: 32px;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-muted);
  transition: color 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    color: var(--color-ink);
  }
`;

const Title = styled.h1`
  font-size: clamp(2.25rem, 4.4vw, 3.25rem);
  letter-spacing: -0.03em;
  margin-bottom: 16px;
`;

const Intro = styled.p`
  max-width: 720px;
  font-size: 1.125rem;
  color: var(--color-muted);
`;

const Updated = styled.p`
  margin-top: 24px;
  font-family: var(--font-mono);
  font-size: 0.8125rem;
  color: var(--color-muted);
`;

const Body = styled(Container)`
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  gap: 72px;
  padding-top: 56px;
  padding-bottom: 112px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 40px;
    padding-bottom: 72px;
  }
`;

const Aside = styled.aside`
  position: sticky;
  top: calc(var(--nav-height) + 32px);
  align-self: start;
  display: grid;
  gap: 32px;

  /* On small screens the policy text comes first, the other policies after it. */
  @media (max-width: 960px) {
    position: static;
    order: 2;
  }
`;

const AsideBlock = styled.nav`
  h2 {
    font-family: var(--font-mono);
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--color-muted);
    margin-bottom: 12px;
  }

  ol,
  ul {
    display: grid;
    gap: 2px;
    list-style: none;
    border-left: 1px solid var(--color-border);
  }

  a {
    display: block;
    margin-left: -1px;
    padding: 6px 0 6px 14px;
    border-left: 2px solid transparent;
    font-size: 0.875rem;
    line-height: 1.45;
    color: var(--color-text);
    transition: color 0.2s ease, border-color 0.2s ease;

    &:hover {
      color: var(--color-ink);
      border-left-color: var(--color-border-strong);
    }

    &.active {
      color: var(--color-primary);
      font-weight: 600;
      border-left-color: var(--color-primary);
    }
  }
`;

const TocBlock = styled(AsideBlock)`
  @media (max-width: 960px) {
    display: none;
  }
`;

// Long-form typography for policy text.
export const Prose = styled.article`
  max-width: 760px;
  font-size: 1.0625rem;
  line-height: 1.75;
  color: var(--color-text);

  section {
    scroll-margin-top: calc(var(--nav-height) + 24px);
  }

  section + section {
    margin-top: 48px;
    padding-top: 48px;
    border-top: 1px solid var(--color-border);
  }

  h2 {
    font-size: 1.5rem;
    margin-bottom: 16px;
  }

  h3 {
    font-size: 1.125rem;
    margin: 28px 0 8px;
  }

  p + p,
  p + ul,
  ul + p {
    margin-top: 14px;
  }

  ul {
    list-style: disc;
    padding-left: 22px;
  }

  li + li {
    margin-top: 6px;
  }

  li::marker {
    color: var(--color-primary);
  }

  strong {
    font-weight: 600;
    color: var(--color-ink);
  }

  a {
    color: var(--color-primary);
    text-decoration: underline;
    text-underline-offset: 3px;
    text-decoration-thickness: 1px;

    &:hover {
      color: var(--color-primary-hover);
    }
  }
`;

// Key/value summary of one processing activity (what, why, how long...).
export const DataCard = styled.div`
  margin-top: 20px;
  padding: 24px;
  background: var(--color-bg-subtle);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);

  h3 {
    margin: 0 0 16px;
  }

  dl {
    display: grid;
    grid-template-columns: 150px minmax(0, 1fr);
    gap: 10px 20px;
    font-size: 0.9375rem;
    line-height: 1.6;
  }

  dt {
    font-weight: 600;
    color: var(--color-ink);
  }

  @media (max-width: 640px) {
    padding: 20px;

    dl {
      grid-template-columns: 1fr;
      gap: 2px;
    }

    dd + dt {
      margin-top: 10px;
    }
  }
`;

const LegalLayout = ({ title, description, intro, path, sections }) => {
  usePageMeta({ title, description, path });

  return (
    <>
      <Header>
        <Container>
          <BackLink to="/">
            <LuArrowLeft aria-hidden="true" />
            Back to home
          </BackLink>
          <Eyebrow>Legal</Eyebrow>
          <Title>{title}</Title>
          {intro && <Intro>{intro}</Intro>}
          <Updated>Last updated: {LAST_UPDATED}</Updated>
        </Container>
      </Header>

      <Body>
        <Aside>
          <TocBlock aria-labelledby="toc-heading">
            <h2 id="toc-heading">On this page</h2>
            <ol>
              {sections.map(section => (
                <li key={section.id}>
                  <a href={`#${section.id}`}>{section.title}</a>
                </li>
              ))}
            </ol>
          </TocBlock>

          <AsideBlock aria-labelledby="legal-pages-heading">
            <h2 id="legal-pages-heading">Legal</h2>
            <ul>
              {legalPages.map(page => (
                <li key={page.path}>
                  <NavLink to={page.path}>{page.label}</NavLink>
                </li>
              ))}
            </ul>
          </AsideBlock>
        </Aside>

        <Prose>
          {sections.map(section => (
            <section key={section.id} id={section.id} aria-labelledby={`${section.id}-title`}>
              <h2 id={`${section.id}-title`}>{section.title}</h2>
              {section.content}
            </section>
          ))}
        </Prose>
      </Body>
    </>
  );
};

export default LegalLayout;
